import { useState, useEffect } from 'react';

import Modal from '../../../components/Modal';
import ECharts from '../../../components/ECharts';
import Radio from '../../../components/Radio';
import TopCompanyLabel from '../../../components/TopCompanyLabel';
import {
  getAxisLabel,
  getMinMax,
  generateMinMaxChartData,
} from '../../../utils/helpers';
import { formatDisplayNumber, formatTradingValue } from '../../../utils/format';
import { HOME_CHART_COLOR } from '../../../constants/colors';
import { getPeriod } from '../../Dashboard/Dashboard.helpers';
import EmptyTag from '../../../components/EmptyTag';

import { transferChartData } from '../ManageInvestments.helpers';

const tradingOptions = [
  {
    value: 'klgd',
    label: 'KLGD',
  },
  {
    value: 'gtgd',
    label: 'GTGD',
  },
];

export const Charts = ({
  handlePeriodClick,
  selectedPeriod,
  periods,
  tradingValue,
  handleTradingChange,
  chartType = 'volume',
  option,
}) => {
  return (
    <div className="w-full">
      <div className="w-full">
        {getPeriod(chartType, handlePeriodClick, selectedPeriod, periods)}
      </div>
      <div className="w-full">
        <ECharts options={option} height="350px" />
        <div className="w-full ml-4">
          <Radio
            name="tradingOption"
            options={tradingOptions}
            defaultValue={tradingValue}
            onChange={handleTradingChange}
          />
        </div>
      </div>
    </div>
  );
};

export default function TradingVolumeAndClosePriceChartModal({
  isOpen,
  onCloseModal,
  isLoading,
  handlePeriodClick,
  selectedPeriod,
  periods,
  isDarkMode,
  chartVolumeData,
  selectedStock,
}) {
  const [isFirstLoad, setFirstLoad] = useState(true);
  const [selectedTrading, setSelectedTrading] = useState(tradingOptions[0]);
  const handleTradingChange = (value) => {
    const item = tradingOptions.find((t) => t.value === value);
    setSelectedTrading(item);
  };

  const getTitle = () => {
    return (
      <div className="flex items-center flex-wrap md:flex-nowrap">
        <div className="flex items-center">
          <span className="whitespace-nowrap">
            Giá & KLGD - {selectedStock?.code}
          </span>
          <TopCompanyLabel label={selectedStock?.label} />
        </div>
        <span className="pl-1">- {selectedStock?.name}</span>
      </div>
    );
  };

  const getChartOption = ({
    dark,
    name,
    xAxisData = [],
    closePriceSeriesData = [],
    tradingSeriesData = [],
    unit = '',
  }) => {
    const intervalRange = 5;
    const interval = Math.floor(xAxisData.length / 2);
    const closePriceMinMax = getMinMax(closePriceSeriesData);
    const minMaxAxisRight = generateMinMaxChartData(
      closePriceMinMax[0],
      closePriceMinMax[1],
      5,
      100
    );
    const otherMinMax = getMinMax(tradingSeriesData);
    const minMaxAxisLeft = generateMinMaxChartData(0, otherMinMax[1], 5);
    const backgroundColor = dark === 'modal' ? '#1a1c23' : '#fff';
    const color = dark === 'modal' ? '#e5e7eb' : '#24262d';
    return {
      backgroundColor,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
          label: {
            formatter: function (params) {
              if (params.axisDimension === 'y') {
                if (params.axisIndex === 0) {
                  return formatDisplayNumber(Math.floor(params.value));
                }

                return formatDisplayNumber(parseFloat(params.value.toFixed(2)));
              }
              return params.value;
            },
          },
        },
      },
      color: [
        dark ? HOME_CHART_COLOR.dark : HOME_CHART_COLOR.light,
        HOME_CHART_COLOR.yellow,
      ],
      grid: {
        top: 35,
        bottom: 35,
        right: 40,
        left: 70,
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          color,
          interval,
          showMaxLabel: true,
          showMinLabel: true,
        },
        splitLine: {
          show: false,
        },
        axisLine: {
          show: true,
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            color,
            formatter: function (value) {
              if (value === 0) {
                return getAxisLabel(
                  value,
                  minMaxAxisLeft.min,
                  minMaxAxisLeft.max
                );
              }

              return `${getAxisLabel(
                value,
                minMaxAxisLeft.min,
                minMaxAxisLeft.max
              )}${unit}`;
            },
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
          },
          min: minMaxAxisLeft.min,
          max: minMaxAxisLeft.max,
          interval: (minMaxAxisLeft.max - minMaxAxisLeft.min) / intervalRange,
        },
        {
          type: 'value',
          axisLabel: {
            color,
            formatter: function (value) {
              return `${getAxisLabel(
                value,
                minMaxAxisRight.min,
                minMaxAxisRight.max
              )}`;
            },
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
          },
          showSymbol: false,
          min: minMaxAxisRight.min,
          max: minMaxAxisRight.max,
          interval: (minMaxAxisRight.max - minMaxAxisRight.min) / intervalRange,
        },
      ],
      series: [
        {
          data: tradingSeriesData,
          type: 'bar',
          name,
          tooltip: {
            valueFormatter: function (value) {
              return `${formatDisplayNumber(value, ',', 0)} ${unit}`;
            },
          },
        },
        {
          data: closePriceSeriesData,
          type: 'line',
          name: 'Giá đóng cửa',
          yAxisIndex: 1,
          showSymbol: false,
        },
      ],
    };
  };

  useEffect(() => {
    if (!chartVolumeData.length && !!periods?.length && isFirstLoad) {
      handlePeriodClick(
        selectedTrading?.value === 'gtgd' ? 'value' : 'volume',
        periods[1].value
      );
    }
    if (chartVolumeData.length) {
      setFirstLoad(false);
    }
  }, [chartVolumeData, periods, isFirstLoad]);

  const renderChart = () => {
    if (isLoading) {
      return <EmptyTag />;
    }

    const { xAxisData, seriesPriceData, seriesOtherData } = transferChartData(
      chartVolumeData,
      selectedTrading.value,
      selectedTrading.value === 'gtgd' ? 1000 : 1
    );
    const tradingSeriesData =
      selectedTrading.value === 'gtgd'
        ? seriesOtherData.map((d) => formatTradingValue(d))
        : seriesOtherData;

    return (
      <div className="w-full">
        <div className="w-full">
          {getPeriod(
            selectedTrading?.value === 'gtgd' ? 'value' : 'volume',
            handlePeriodClick,
            selectedPeriod,
            periods
          )}
        </div>
        <div className="w-full">
          {chartVolumeData.length ? (
            <ECharts
              options={getChartOption({
                isDarkMode,
                name: selectedTrading?.label,
                xAxisData,
                closePriceSeriesData: seriesPriceData,
                tradingSeriesData,
                unit: selectedTrading.value === 'gtgd' ? ' tỷ' : ' cp',
              })}
              height="350px"
            />
          ) : (
            <div className="min-h-[350px] p-4 w-full relative text-gray-600 dark:text-gray-400 flex items-center justify-center">
              Không có dữ liệu
            </div>
          )}
          <div className="w-full ml-4">
            <Radio
              name="tradingOption"
              options={tradingOptions}
              defaultValue={selectedTrading?.value}
              onChange={handleTradingChange}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={onCloseModal}
      title={getTitle()}
      isLoading={isLoading}
      mainContentClassName="modal-center max-w-[96vw] md:max-w-[70vw] m-auto w-full"
      childrenWrapperClassName=""
    >
      {renderChart()}
    </Modal>
  );
}
